import { useEffect as useEffectOrig, useState as useStateOrig } from 'react';
import { quranApi } from '../../services/api';
// Helper: fetch verse details for a list of occurrences
function useVerseDetails(occurrences) {
  const [verses, setVerses] = useStateOrig({});
  useEffectOrig(() => {
    if (!occurrences || occurrences.length === 0) return;
    let cancelled = false;
    const fetchAll = async () => {
      const results = {};
      await Promise.all(
        occurrences.map(async (occ) => {
          const { surah, ayah } = occ;
          try {
            const res = await quranApi.getVerseDetail(surah, ayah);
            results[`${surah}:${ayah}`] = res.data;
          } catch {
            results[`${surah}:${ayah}`] = null;
          }
        })
      );
      if (!cancelled) setVerses(results);
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [JSON.stringify(occurrences)]);
  return verses;
}
/**
 * QuranWordAnalysisPanel
 *
 *  Side panel (desktop right-edge) / bottom sheet (mobile) showing the
 *  lexical analysis of the currently selected word.
 *
 *  Always honest: any field missing in the imported data is rendered as
 *  "Non disponible". We never invent linguistic information.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Copy, Hash, Layers, GitBranch, BookOpen, ListMinus, Volume2,
  ExternalLink, Loader2, AlertCircle,
} from 'lucide-react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { lexicalApi } from '../../services/api';
import { usePreferences } from '../../context/PreferencesContext';
import { quranAudio } from '../../audio/quranAudio';
import { toast } from 'sonner';

const NA = ({ children = 'Non disponible' }) => (
  <span className="text-text-muted italic text-sm">{children}</span>
);

const Field = ({ label, children, testid }) => (
  <div className="flex flex-col gap-1" data-testid={testid}>
    <span className="text-xs uppercase tracking-wider text-text-muted">{label}</span>
    <div className="text-sm text-text-primary">{children}</div>
  </div>
);

// -- Part-of-speech label dictionary (display only — pulled from data) --
const POS_LABELS_FR = {
  N: 'Nom', PN: 'Nom propre', V: 'Verbe', P: 'Préposition',
  ADJ: 'Adjectif', PRON: 'Pronom', ADV: 'Adverbe',
  CONJ: 'Conjonction', DET: 'Article', PART: 'Particule',
  REL: 'Relatif', DEM: 'Démonstratif', INTG: 'Interrogatif',
  NEG: 'Négation', T: 'Temps', LOC: 'Lieu', NUM: 'Nombre',
};

const formatPOS = (pos) => {
  if (!pos) return null;
  const cleaned = String(pos).toUpperCase();
  return POS_LABELS_FR[cleaned] || pos;
};

// Aggregate multi-segment data of a clicked word into a single "view"
function summarizeSegments(segments) {
  if (!segments || !segments.length) return null;
  // Pick the "main" segment = the one with the most lexical info, then
  // combine surface forms for display.
  const main = segments.reduce((best, s) => {
    const score = (s.lemma ? 2 : 0) + (s.root ? 2 : 0) + (s.partOfSpeech ? 1 : 0);
    const bestScore = (best.lemma ? 2 : 0) + (best.root ? 2 : 0) + (best.partOfSpeech ? 1 : 0);
    return score > bestScore ? s : best;
  }, segments[0]);

  return {
    surface: segments.map((s) => s.textUthmani).join(''),
    main,
    segments,
    translations: segments
      .map((s) => s.translation?.fr || s.translation?.en || s.translation?.ar)
      .filter(Boolean),
  };
}

export const QuranWordAnalysisPanel = ({
    lemmaOccurrences = [], // [{ surah, ayah, wordIndex }]
  open,
  onClose,
  surahNumber,
  ayahKey,
  wordIndex,
  initialSegments,
  initialDisplayText,
  ayahArabic,
}) => {
  const { t } = useTranslation();
    // Fetch verse details for all occurrences (if provided)
    const versesByKey = useVerseDetails(lemmaOccurrences);
  const { language } = usePreferences();
  const navigate = useNavigate();

  const [segments, setSegments] = useState(initialSegments || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lemmaCount, setLemmaCount] = useState(null);
  const [rootCount, setRootCount] = useState(null);
  const [wordGloss, setWordGloss] = useState(null);
  const [lemmaGloss, setLemmaGloss] = useState(null);
  const [rootGloss, setRootGloss] = useState(null);

  // (Re)load full word data when the panel opens or selection changes.
  useEffect(() => {
    if (!open || !ayahKey || !wordIndex) return;
    let alive = true;
    setError(null);
    setSegments(initialSegments || []);
    setLemmaCount(null);
    setRootCount(null);
    setWordGloss(null);
    setLemmaGloss(null);
    setRootGloss(null);
    setLoading(true);
    lexicalApi.getWord(ayahKey, wordIndex)
      .then((res) => {
        if (!alive) return;
        const segs = res.data?.segments || [];
        setSegments(segs);
        const main = segs.find((s) => s.lemma || s.root) || segs[0] || {};
        const lem = main.normalizedLemma;
        const rt = main.normalizedRoot;
        const wd = main.normalizedText;
        if (wd) {
          lexicalApi.getGloss('word', wd, language)
            .then((r) => alive && setWordGloss(r.data?.available ? r.data : null))
            .catch(() => {});
        }
        if (lem) {
          lexicalApi.getLemma(lem, language).then((r) => {
            if (alive) {
              setLemmaCount(r.data?.occurrenceCount ?? null);
              setLemmaGloss(r.data?.gloss || null);
            }
          }).catch(() => {});
        }
        if (rt) {
          lexicalApi.getRoot(rt, language).then((r) => {
            if (alive) {
              setRootCount(r.data?.occurrenceCount ?? null);
              setRootGloss(r.data?.gloss || null);
            }
          }).catch(() => {});
        }
      })
      .catch((e) => {
        if (!alive) return;
        if (e?.response?.status === 404) {
          setSegments([]);
        } else {
          setError("Impossible de charger l'analyse lexicale.");
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, ayahKey, wordIndex, initialSegments, language]);

  const summary = useMemo(() => summarizeSegments(segments), [segments]);
  const main = summary?.main;

  const copyText = (text, label = 'Mot') => {
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copié`),
      () => toast.error('Impossible de copier'),
    );
  };

  const playAyah = () => {
    if (!surahNumber || !ayahKey) return;
    const [, ayahNumber] = ayahKey.split(':').map(Number);
    quranAudio.playAyah({ surahNumber, ayahNumber, ayahKey });
  };

  return (
    <>
      {lemmaOccurrences && lemmaOccurrences.length > 0 && (
        <div className="space-y-4 mt-4">
              <div className="text-xs uppercase tracking-wider text-text-muted mb-2">Occurrences dans le Coran</div>
              {lemmaOccurrences.map((occ, idx) => {
                const key = `${occ.surah}:${occ.ayah}`;
                const verse = versesByKey[key];
                return (
                  <div key={key} className="p-3 rounded bg-background/60 border border-border mb-2">
                    <div className="flex items-center gap-2 mb-1">
                      <button
                        className="text-primary underline hover:text-accent transition"
                        onClick={() => navigate(`/quran/${occ.surah}#verse-${occ.ayah}`)}
                      >
                        {`Sourate ${occ.surah}, Verset ${occ.ayah}`}
                      </button>
                      <span className="text-xs text-text-muted">(mot {occ.wordIndex})</span>
                    </div>
                    {verse ? (
                      <>
                        <div className="font-arabic text-lg text-text-primary mb-1" dir="rtl">{verse.arabic}</div>
                        <div className="text-sm text-text-secondary italic">{verse.translation?.fr || <NA>Texte français non disponible</NA>}</div>
                      </>
                    ) : (
                      <NA>Texte du verset non disponible</NA>
                    )}
                  </div>
                );
              })}
            </div>
          )}
    <Sheet open={open} onOpenChange={(o) => !o && onClose && onClose()}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md md:max-w-lg overflow-y-auto bg-surface border-border"
        data-testid="quran-word-analysis-panel"
      >
        <SheetHeader className="text-left mb-2">
          <SheetTitle className="font-serif text-text-primary flex items-center gap-2">
            Analyse lexicale
            {loading && <Loader2 className="w-4 h-4 animate-spin text-primary" />}
          </SheetTitle>
          <SheetDescription className="text-text-muted text-xs">
            {ayahKey} · mot {wordIndex}
          </SheetDescription>
        </SheetHeader>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-amber-900/20 border border-amber-700/40 text-amber-200 text-sm mb-4">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!error && (
          <div className="space-y-5">
            {/* Surface text (always shown, even when no morphology) */}
            <div className="p-4 rounded-md bg-background/60 border border-border text-center">
              <p
                className="quran-arabic-text text-primary"
                dir="rtl"
                data-testid="word-surface"
              >
                {summary?.surface || initialDisplayText || ''}
              </p>
              {main?.transliteration && (
                <p className="text-text-muted text-xs italic mt-2">
                  {main.transliteration}
                </p>
              )}
            </div>

            {/* Empty-state when no morphology data exists for this word */}
            {!loading && segments.length === 0 && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-900/20 border border-amber-700/40 text-amber-200 text-sm" data-testid="no-lexical-data">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <div className="font-medium">Aucune donnée lexicale pour ce mot</div>
                  <div className="text-amber-200/80 text-xs">
                    La source morphologique n'a pas encore été importée pour ce verset.
                    Une fois l'import effectué (script <code className="bg-black/30 px-1 rounded">scripts.import_morphology</code>),
                    la racine, le lemme et l'analyse grammaticale apparaîtront ici.
                  </div>
                </div>
              </div>
            )}

            {/* Translations + word gloss */}
            {(summary?.translations?.length > 0 || wordGloss) && (
              <Field label="Traduction / sens du mot" testid="field-translation">
                {summary?.translations?.length > 0 && (
                  <p className="text-text-secondary leading-relaxed mb-1">
                    {summary.translations.join(' · ')}
                  </p>
                )}
                {wordGloss?.shortMeaning && (
                  <p className="text-text-primary text-sm">{wordGloss.shortMeaning}</p>
                )}
                {wordGloss?.definition && (
                  <p className="text-text-muted text-xs mt-1">{wordGloss.definition}</p>
                )}
                {!summary?.translations?.length && !wordGloss?.shortMeaning && <NA />}
              </Field>
            )}

            {/* POS / lemma / root grid */}
            <div className="grid grid-cols-1 gap-3">
              <Field label="Type grammatical" testid="field-pos">
                {main?.partOfSpeech ? (
                  <Badge className="bg-primary/15 text-primary border-primary/30">
                    {formatPOS(main.partOfSpeech)} <span className="opacity-50 ml-1">({main.partOfSpeech})</span>
                  </Badge>
                ) : <NA />}
              </Field>

              <Field label="Lemme (forme de citation)" testid="field-lemma">
                {main?.lemma ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-arabic text-xl text-text-primary" dir="rtl">{main.lemma}</span>
                      {lemmaCount != null && (
                        <Badge variant="outline" className="text-xs">
                          {lemmaCount}× dans le Coran
                        </Badge>
                      )}
                    </div>
                    {lemmaGloss?.shortMeaning ? (
                      <p className="text-sm text-text-secondary">{lemmaGloss.shortMeaning}</p>
                    ) : (
                      <p className="text-xs text-text-muted italic">Définition non disponible</p>
                    )}
                  </div>
                ) : <NA />}
              </Field>

              <Field label="Racine" testid="field-root">
                {main?.root ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-arabic text-xl text-text-primary tracking-widest" dir="rtl">
                        {main.root}
                      </span>
                      {rootCount != null && (
                        <Badge variant="outline" className="text-xs">
                          {rootCount}× dans le Coran
                        </Badge>
                      )}
                    </div>
                    {rootGloss?.shortMeaning ? (
                      <p className="text-sm text-text-secondary">{rootGloss.shortMeaning}</p>
                    ) : (
                      <p className="text-xs text-text-muted italic">Sens non disponible</p>
                    )}
                  </div>
                ) : <NA />}
              </Field>
            </div>

            {/* Multi-segment breakdown if present */}
            {segments.length > 1 && (
              <div className="space-y-2">
                <div className="text-xs uppercase tracking-wider text-text-muted">
                  Segments morphologiques
                </div>
                <div className="space-y-2">
                  {segments.map((s, i) => (
                    <div
                      key={i}
                      className="p-3 rounded bg-background/60 border border-border flex items-center justify-between gap-3"
                      data-testid={`segment-${i}`}
                    >
                      <div className="flex flex-col">
                        <span className="font-arabic text-lg text-text-primary" dir="rtl">
                          {s.textUthmani}
                        </span>
                        <span className="text-xs text-text-muted">
                          {formatPOS(s.partOfSpeech) || '—'}
                          {s.lemma ? ` · lem. ${s.lemma}` : ''}
                        </span>
                      </div>
                      <Layers className="w-4 h-4 text-primary/60" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2 pt-3 border-t border-border">
              <Button
                size="sm"
                variant="outline"
                onClick={() => main?.lemma && navigate(`/lexical/lemma/${encodeURIComponent(main.normalizedLemma || main.lemma)}`)}
                disabled={!main?.lemma}
                className="border-border"
                data-testid="explore-lemma-btn"
              >
                <BookOpen className="w-4 h-4 mr-1" /> Explorer le lemme
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => main?.root && navigate(`/lexical/root/${encodeURIComponent(main.normalizedRoot || main.root.replace(/\s+/g, ''))}`)}
                disabled={!main?.root}
                className="border-border"
                data-testid="explore-root-btn"
              >
                <GitBranch className="w-4 h-4 mr-1" /> Explorer la racine
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => main?.normalizedText &&
                  navigate(`/lexical/search?q=${encodeURIComponent(main.normalizedText)}&mode=exact_word`)}
                disabled={!main?.normalizedText}
                className="border-border"
                data-testid="explore-occurrences-btn"
              >
                <ListMinus className="w-4 h-4 mr-1" /> Toutes les occurrences
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={playAyah}
                className="border-border"
                data-testid="play-ayah-btn"
              >
                <Volume2 className="w-4 h-4 mr-1" /> Écouter le verset
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => copyText(summary?.surface || '', 'Mot')}
                className="text-text-muted hover:text-primary"
                data-testid="copy-word-btn"
              >
                <Copy className="w-4 h-4 mr-1" /> Copier
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => navigate(`/quran/${surahNumber}#verse-${ayahKey?.split(':')[1]}`)}
                className="text-text-muted hover:text-primary"
                data-testid="open-verse-btn"
              >
                <ExternalLink className="w-4 h-4 mr-1" /> Ouvrir le verset
              </Button>
            </div>

            {/* Source */}
            {main?.source && (
              <div className="text-xs text-text-muted pt-3 border-t border-border space-y-1">
                <div className="flex items-center gap-1">
                  <Hash className="w-3.5 h-3.5" />
                  Source : <span className="text-text-secondary">
                    {main.source.provider}
                    {main.source.version ? ` (${main.source.version})` : ''}
                  </span>
                  {main.source.licenseStatus && (
                    <Badge variant="outline" className="text-xs ml-1">
                      {main.source.licenseStatus}
                    </Badge>
                  )}
                </div>
                {main.reviewStatus && main.reviewStatus !== 'approved' && (
                  <div className="text-amber-300/80 italic">
                    Statut de revue : {main.reviewStatus}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
    </>
  );
};
